import React, { useContext, useState } from "react";
import LangSelector from "./LangSelector";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  Box,
  Stack,
  Divider,
  Typography
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Business from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { AuthContext } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import Categories from "./Categories";

const NavBar = () => {
  const { isLoggedIn, handleShowLogin, handleLogout } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: 'Главная', icon: <HomeIcon sx={{ fontSize: 18 }} />, path: '/' },
    { label: 'Избранное', icon: <FavoriteIcon sx={{ fontSize: 18 }} />, path: '/favorites' },
    { label: 'История', icon: <HistoryIcon sx={{ fontSize: 18 }} />, path: '/recent' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('?')[0]);
  };

  return (
    <>
      {/* 🔥 NAVBAR */}
      <AppBar
        position="fixed"
        elevation={0}
        style={{
          top: 0,
          left: 0,
          right: 0,
          backdropFilter: "blur(25px)",
          backgroundColor: "rgba(255, 255, 255, 0.65)", // Белый полупрозрачный
          borderBottom: "1px solid rgba(255, 255, 255, 0.15)", // Белая полупрозрачная граница
          boxShadow: "0 1px 20px rgba(255, 255, 255, 0.05)", // Очень легкая белая тень
          zIndex: 1100,
          transition: "none !important",
        }}
        sx={{
          '&.MuiAppBar-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.65) !important',
            backdropFilter: 'blur(25px) !important',
          }
        }}
      >
        <Toolbar
          sx={{
            maxWidth: "1200px",
            width: "100%",
            mx: "auto",
            px: { xs: 2, md: 3 },
            backgroundColor: "transparent !important",
            '&.MuiToolbar-root': {
              backgroundColor: 'transparent !important',
            }
          }}
        >
          {/* LEFT */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Logo />
            <LangSelector />
          </Box>

          {/* DESKTOP — навигация */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: "none", md: "flex" }, alignItems: 'center' }}
          >
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                startIcon={item.icon}
                sx={{
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.8,
                  textTransform: 'none',
                  fontWeight: isActive(item.path) ? 700 : 500,
                  fontSize: '14px',
                  color: isActive(item.path) ? '#6366f1' : '#64748b',
                  background: isActive(item.path) ? 'rgba(99,102,241,0.08)' : 'transparent',
                  '&:hover': {
                    background: 'rgba(99,102,241,0.12)',
                    color: '#6366f1',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {/* Кнопка Компании — всегда видна */}
            <Button
              onClick={() => navigate("/companies")}
              sx={{
                borderRadius: "999px",
                px: 2,
                textTransform: "none",
                background: "rgba(34, 197, 94, 0.1)",
                color: "#16a34a",
                mr: 1,
                "&:hover": {
                  background: "#16a34a",
                  color: "white",
                },
              }}
            >
              <Business sx={{ mr: 1, fontSize: 18 }} />
              Компании
            </Button>

            {!isLoggedIn ? (
              <Button
                onClick={handleShowLogin}
                sx={{
                  borderRadius: "999px",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  color: "white",
                  boxShadow: "0 4px 14px rgba(99,102,241,0.4)",

                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 25px rgba(99,102,241,0.5)",
                  },
                }}
              >
                Вход
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/profile")}
                  sx={{
                    borderRadius: "999px",
                    px: 2,
                    textTransform: "none",
                    "&:hover": {
                      background: "#6366f1",
                      color: "white",
                    },
                  }}
                >
                  Профиль
                </Button>

                <Button
                  onClick={() => navigate("/employer/dashboard")}
                  sx={{
                    borderRadius: "999px",
                    px: 2,
                    textTransform: "none",
                    background: "rgba(99,102,241,0.1)",
                    color: "#6366f1",
                    "&:hover": {
                      background: "#6366f1",
                      color: "white",
                    },
                  }}
                >
                  <Business sx={{ mr: 1, fontSize: 18 }} />
                  Для работодателей
                </Button>

                <Button
                  onClick={handleLogout}
                  sx={{
                    borderRadius: "999px",
                    px: 2,
                    textTransform: "none",
                    color: "error.main",
                    "&:hover": {
                      background: "#fee2e2",
                    },
                  }}
                >
                  Выйти
                </Button>
              </>
            )}
          </Stack>

          {/* MOBILE */}
          <IconButton
            sx={{ display: { xs: "flex", md: "none" } }}
            onClick={() => setOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* 🔥 MOBILE DRAWER */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ width: 320, maxWidth: "100vw", p: 3 }}>

          <Stack spacing={2.5}>
            <Logo />

            {/* Навигация */}
            <Box>
              <Typography variant="h6" fontWeight={700} mb={1.5}>
                Меню
              </Typography>
              
              <Stack spacing={0.5}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    fullWidth
                    onClick={() => {
                      navigate(item.path);
                      setOpen(false);
                    }}
                    startIcon={item.icon}
                    sx={{
                      justifyContent: 'flex-start',
                      borderRadius: '8px',
                      py: 1.2,
                      px: 2,
                      textTransform: 'none',
                      fontWeight: isActive(item.path) ? 700 : 500,
                      fontSize: '15px',
                      color: isActive(item.path) ? '#6366f1' : '#334155',
                      background: isActive(item.path) ? 'rgba(99,102,241,0.08)' : 'transparent',
                      '&:hover': {
                        background: 'rgba(99,102,241,0.12)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Button
                fullWidth
                onClick={() => {
                  navigate("/companies");
                  setOpen(false);
                }}
                startIcon={<Business />}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: '8px',
                  py: 1.2,
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#16a34a',
                  mb: 1,
                  background: 'rgba(34, 197, 94, 0.08)',
                  '&:hover': {
                    background: 'rgba(34, 197, 94, 0.15)',
                  },
                }}
              >
                Компании
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
                Аккаунт
              </Typography>
              {!isLoggedIn ? (
              <Button
                fullWidth
                onClick={() => {
                  handleShowLogin();
                  setOpen(false);
                }}
                sx={{
                  borderRadius: "999px",
                  py: 1.2,
                  background:
                    "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  color: "white",
                }}
              >
                Вход
              </Button>
            ) : (
              <>
                <Button
                  fullWidth
                  onClick={() => {
                    navigate("/profile");
                    setOpen(false);
                  }}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  Профиль
                </Button>

                <Button
                  fullWidth
                  onClick={() => {
                    handleLogout();
                    setOpen(false);
                  }}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'error.main' }}
                >
                  Выйти
                </Button>
              </>
            )}
            </Box>

            <Divider />

            <Categories
              variant="sidebar"
              title="Категории"
              compact
              onSelect={() => setOpen(false)}
            />
          </Stack>

        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;